import { useState, useMemo } from 'react';
import { CSVLink } from 'react-csv';
import { FaSort, FaSortUp, FaSortDown, FaTrash, FaEdit, FaDownload } from 'react-icons/fa';

function Table({ columns, data, actions = [] }) {
  const [searchTerm, setSearchTerm] = useState('');
  const [searchField, setSearchField] = useState(''); // For search by field
  const [sortConfig, setSortConfig] = useState({ key: '', direction: '' });
  const [currentPage, setCurrentPage] = useState(1);
  const [entriesPerPage, setEntriesPerPage] = useState(10);

  // Prepare CSV data for export
  const csvData = useMemo(() => {
    const headers = columns.map((col) => col.label);
    const rows = data.map((item) =>
      columns.map((col) => (col.render ? col.render(item) : item[col.key] || 'N/A'))
    );
    return [headers, ...rows];
  }, [columns, data]);

  // Handle sorting
  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  // Sort data
  const sortedData = useMemo(() => {
    if (!sortConfig.key) return [...data];
    return [...data].sort((a, b) => {
      const aValue = a[sortConfig.key] || '';
      const bValue = b[sortConfig.key] || '';
      if (sortConfig.direction === 'asc') {
        return aValue > bValue ? 1 : -1;
      }
      return aValue < bValue ? 1 : -1;
    });
  }, [data, sortConfig]);

  // Handle search and filter
  const filteredData = useMemo(() => {
    if (!searchTerm) return sortedData;
    return sortedData.filter((item) => {
      if (searchField) {
        // Search by specific field
        const value = item[searchField] || '';
        return value.toString().toLowerCase().includes(searchTerm.toLowerCase());
      }
      // Global search across all columns
      return columns.some((col) => {
        const value = col.render ? col.render(item) : item[col.key] || '';
        return value.toString().toLowerCase().includes(searchTerm.toLowerCase());
      });
    });
  }, [sortedData, searchTerm, searchField, columns]);

  // Pagination logic
  const totalEntries = filteredData.length;
  const totalPages = Math.ceil(totalEntries / entriesPerPage);
  const paginatedData = filteredData.slice(
    (currentPage - 1) * entriesPerPage,
    currentPage * entriesPerPage
  );

  // Handle page change
  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  return (
    <div className=" dark:bg-gray-900">
      {/* Table Controls */}
      <div className="flex flex-col sm:flex-row justify-between items-center gap-3 mb-4">
  <div className="flex items-center gap-2">
    <label className="text-xs sm:text-sm text-gray-700 dark:text-gray-300 flex items-center gap-1">
      Show
      <select
        value={entriesPerPage}
        onChange={(e) => {
          setEntriesPerPage(Number(e.target.value));
          setCurrentPage(1); // Reset to first page
        }}
        className="ml-1 sm:ml-2 p-1 sm:p-2 border rounded-lg bg-gray-50 dark:bg-gray-600 dark:text-white dark:border-gray-600 focus:outline-none focus:ring-2 focus:ring-blue-500 text-xs sm:text-sm"
      >
        <option value={5}>5</option>
        <option value={10}>10</option>
        <option value={25}>25</option>
        <option value={50}>50</option>
      </select>
      entries
    </label>
  </div>
  <div className="flex flex-col sm:flex-row items-center gap-2 w-full sm:w-auto">
    <div className="flex items-center gap-1 w-full">
      <select
        value={searchField}
        onChange={(e) => setSearchField(e.target.value)}
        className="p-1 sm:p-2 border rounded-lg bg-gray-50 dark:bg-gray-600 dark:text-white dark:border-gray-600 focus:outline-none focus:ring-2 focus:ring-blue-500 w-full sm:w-28 text-xs sm:text-sm"
      >
        <option value="">All Fields</option>
        {columns.map((col) => (
          <option key={col.key} value={col.key}>
            {col.label}
          </option>
        ))}
      </select>
      <input
        type="text"
        placeholder="Search..."
        value={searchTerm}
        onChange={(e) => {
          setSearchTerm(e.target.value);
          setCurrentPage(1); // Reset to first page
        }}
        className="p-1 sm:p-2 border rounded-lg bg-gray-50 dark:bg-gray-600 dark:text-white dark:border-gray-600 focus:outline-none focus:ring-2 focus:ring-blue-500 w-full sm:w-28 text-xs sm:text-sm"
      />
    </div>
    <CSVLink
      data={csvData}
      filename="table-data.csv"
      className="flex items-center gap-1 p-1 sm:p-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition duration-200 w-full sm:w-auto justify-center text-xs sm:text-sm"
    >
      <FaDownload className="text-xs sm:text-sm" /> Export
    </CSVLink>
  </div>
</div>

      {/* Table */}
      <div className="w-full max-w-screen-2xl overflow-x-auto overscroll-y-auto bg-white dark:bg-gray-800 rounded-lg shadow-md">
        <table className="w-full text-sm text-gray-700 dark:text-gray-200">
          <thead>
            <tr className=" dark:bg-gray-700 text-left">
              <th className="p-4">
                <input type="checkbox" className="rounded" />
              </th>
              {columns.map((col) => (
                <th
                  key={col.key}
                  className="p-4 font-medium cursor-pointer hover:bg-gray-200 dark:hover:bg-gray-600"
                  onClick={() => handleSort(col.key)}
                >
                  <div className="flex items-center space-x-1">
                    <span>{col.label}</span>
                    {sortConfig.key === col.key ? (
                      sortConfig.direction === 'asc' ? (
                        <FaSortUp />
                      ) : (
                        <FaSortDown />
                      )
                    ) : (
                      <FaSort className="text-gray-400" />
                    )}
                  </div>
                </th>
              ))}
              {actions.length > 0 && <th className="p-4 font-medium">Action</th>}
            </tr>
          </thead>
          <tbody>
            {paginatedData.length === 0 ? (
              <tr>
                <td
                  colSpan={columns.length + 2}
                  className="p-4 text-center text-gray-500 dark:text-gray-400"
                >
                  No data available
                </td>
              </tr>
            ) : (
              paginatedData.map((item, index) => (
                <tr
                  key={item._id || index}
                  className={`border-b dark:border-gray-600 ${
                    index % 2 === 0 ? 'bg-white dark:bg-gray-800' : 'bg-gray-50 dark:bg-gray-700'
                  } hover: dark:hover:bg-gray-600 transition duration-100`}
                >
                  <td className="p-4">
                    <input type="checkbox" className="rounded" />
                  </td>
                  {columns.map((col) => (
                    <td key={col.key} className="p-4">
                      {col.key === 'status' ? (
                        <span
                          className={`px-2 py-1 rounded-full text-xs font-medium ${
                            item[col.key] === 'Hired'
                              ? 'bg-green-100 text-green-700'
                              : 'bg-red-100 text-red-700'
                          }`}
                        >
                          {item[col.key] || 'N/A'}
                        </span>
                      ) : col.render ? (
                        col.render(item)
                      ) : (
                        item[col.key] || 'N/A'
                      )}
                    </td>
                  ))}
                  {actions.length > 0 && (
                    <td className="p-4 flex space-x-2">
                      {actions.map((action) => (
                        <button
                          key={action.label}
                          onClick={() => action.onClick(item)}
                          className={`p-2 rounded-full ${action.className}`}
                        >
                          {action.label === 'Edit' ? (
                            <FaEdit className="text-blue-500" />
                          ) : (
                            <FaTrash className="text-red-500" />
                          )}
                        </button>
                      ))}
                    </td>
                  )}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <div className="flex flex-col sm:flex-row justify-between items-center mt-4 text-sm text-gray-700 dark:text-gray-300">
        <div>
          Showing {(currentPage - 1) * entriesPerPage + 1} to{' '}
          {Math.min(currentPage * entriesPerPage, totalEntries)} of {totalEntries} entries
        </div>
        <div className="flex space-x-2 mt-4 sm:mt-0">
          <button
            onClick={() => handlePageChange(currentPage - 1)}
            disabled={currentPage === 1}
            className="px-4 py-2 border rounded disabled:opacity-50 dark:bg-gray-600 dark:text-white"
          >
            Previous
          </button>
          {[...Array(totalPages)].map((_, i) => (
            <button
              key={i + 1}
              onClick={() => handlePageChange(i + 1)}
              className={`px-4 py-2 border rounded ${
                currentPage === i + 1
                  ? 'bg-blue-600 text-white'
                  : 'dark:bg-gray-600 dark:text-white'
              }`}
            >
              {i + 1}
            </button>
          ))}
          <button
            onClick={() => handlePageChange(currentPage + 1)}
            disabled={currentPage === totalPages}
            className="px-4 py-2 border rounded disabled:opacity-50 dark:bg-gray-600 dark:text-white"
          >
            Next
          </button>
        </div>
      </div>
    </div>
  );
}

export default Table;